/**
 * Veraso Landing page language picker.
 *
 * Resolution order (highest → lowest priority):
 *   1. localStorage `lang` override (set by manual FI/EN toggle in the header)
 *   2. Hostname:
 *        verasoreports.com → en
 *        veraso.fi         → fi
 *   3. Browser language (navigator.language starts with "fi" → fi)
 *   4. Default → fi
 */
const LS_KEY = "veraso:lang";

/**
 * Detect the active landing-page language.
 *
 * Production behaviour:
 *   - veraso.fi          → FI (also force-sync to localStorage)
 *   - verasoreports.com  → EN (also force-sync to localStorage)
 *
 * The force-sync is intentional: a visitor arriving on veraso.fi expects
 * the registration flow and the app to also be in Finnish, regardless of
 * any stale `veraso:lang=en` value left by a previous test session. The
 * landing-page toggle still works — it just rewrites localStorage on click.
 *
 * Preview / localhost: respect existing localStorage so we can preview EN
 * without changing hostname.
 */
export const detectLandingLang = () => {
  if (typeof window === "undefined") return "fi";

  const host = (window.location.hostname || "").toLowerCase();
  const isProdFi = host === "veraso.fi" || host === "www.veraso.fi";
  const isProdEn = host === "verasoreports.com" || host === "www.verasoreports.com";

  // Production: hostname wins, localStorage gets re-synced.
  if (isProdFi) {
    try { window.localStorage.setItem(LS_KEY, "fi"); } catch { /* ignore */ }
    return "fi";
  }
  if (isProdEn) {
    try { window.localStorage.setItem(LS_KEY, "en"); } catch { /* ignore */ }
    return "en";
  }

  // Preview / local: respect any explicit choice.
  try {
    const stored = window.localStorage.getItem(LS_KEY);
    if (stored === "fi" || stored === "en") return stored;
  } catch { /* ignore */ }

  return "fi";
};

export const setLandingLang = (lang) => {
  if (lang !== "fi" && lang !== "en") return;
  try { window.localStorage.setItem(LS_KEY, lang); } catch { /* ignore */ }
};

/** Apply <html lang>, <title>, <meta description>, OpenGraph + hreflang.
 *  Called from the Landing component on mount and whenever the user toggles. */
export const applyLandingHead = (lang) => {
  if (typeof document === "undefined") return;

  const isFi = lang === "fi";

  document.documentElement.lang = isFi ? "fi" : "en";

  const TITLE = {
    fi: "Veraso Reports — Kenttäraportointi ja huoltoraportit puhelimella",
    en: "Veraso Reports — Field reporting and maintenance reports on your phone",
  };
  document.title = TITLE[lang] || TITLE.fi;

  const DESC = {
    fi: "Veraso Reports — kenttäraportointi ja mobiiliraportointi suomalaisille kenttätyöyrityksille. Huoltoraportti, työraportti, tarkastusraportti ja PDF-raportti suoraan puhelimella työn aikana.",
    en: "Veraso Reports — field reporting and mobile reporting for service teams. Maintenance reports, inspection reports, HVAC reports and PDF reports created on your phone while the work is happening.",
  };
  const KEYS = {
    fi: "kenttäraportointi, huoltoraportti, työraportti, tarkastusraportti, mobiiliraportointi, raportointi kentällä, raportointisovellus, huoltoraportointi, sähköinen raportointi, PDF-raportti, IV-huoltoraportti, LVI-huoltoraportti, sähkötarkastusraportti, kiinteistöhuolto, kattotarkastusraportti",
    en: "field reporting, maintenance reports, inspection reports, mobile reporting, HVAC reporting, PDF reports, field service reports, electrical inspection report, property maintenance report, roof inspection report, work reports",
  };
  const OG_TITLE = {
    fi: "Veraso Reports — Kenttäraportointi puhelimella",
    en: "Veraso Reports — Field reporting on your phone",
  };
  const OG_DESC = {
    fi: "Huoltoraportit, tarkastusraportit ja työraportit suoraan kentällä. Selkeät PDF-raportit yrityksen omalla ilmeellä.",
    en: "Maintenance, inspection and work reports created in the field. Clean PDF reports with your own company branding.",
  };

  const upsertMeta = (selector, attrName, attrValue) => {
    let el = document.querySelector(selector);
    if (!el) {
      el = document.createElement("meta");
      const [k, v] = selector.replace("meta[", "").replace("]", "").split("=");
      el.setAttribute(k, (v || "").replace(/^"|"$/g, ""));
      document.head.appendChild(el);
    }
    el.setAttribute(attrName, attrValue);
  };

  upsertMeta('meta[name="description"]', "content", DESC[lang]);
  upsertMeta('meta[name="keywords"]', "content", KEYS[lang]);
  upsertMeta('meta[property="og:title"]', "content", OG_TITLE[lang]);
  upsertMeta('meta[property="og:description"]', "content", OG_DESC[lang]);
  upsertMeta('meta[property="og:locale"]', "content", isFi ? "fi_FI" : "en_US");

  // og:image — use the FI banner on veraso.fi (and as fallback in preview),
  // EN banner on verasoreports.com.
  const ogImg = isFi
    ? "https://veraso.fi/brand/og-veraso-fi.jpg"
    : "https://verasoreports.com/brand/og-veraso-en.jpg";
  upsertMeta('meta[property="og:image"]', "content", ogImg);
  upsertMeta('meta[property="og:image:width"]', "content", "1200");
  upsertMeta('meta[property="og:image:height"]', "content", "630");
  upsertMeta('meta[property="og:image:type"]', "content", "image/jpeg");
  upsertMeta('meta[name="twitter:card"]', "content", "summary_large_image");
  upsertMeta('meta[name="twitter:image"]', "content", ogImg);

  // hreflang alternates — point search engines at the right language-domain pair
  const ensureHrefLang = (hl, href) => {
    let link = document.querySelector(`link[rel="alternate"][hreflang="${hl}"]`);
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hreflang", hl);
      document.head.appendChild(link);
    }
    link.setAttribute("href", href);
  };
  ensureHrefLang("fi", "https://veraso.fi/");
  ensureHrefLang("en", "https://verasoreports.com/");
  ensureHrefLang("x-default", "https://veraso.fi/");
};
